import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import moment from 'moment';
import OrderDetails from './OrderDetails.jsx';
import UpdateTrackingInfo from './UpdateTrackingInfo';
import DownloadOrderItems from './DownloadOrderItems';

import { Theme } from '../Styles';
import { ArrowDownIcon, DSOIcon, CalendarIcon, StatusIcon, ProcessingIcon, AddIcon, DownloadIcon, RefreshInventoryIcon } from './Styles/Icons';
import { RootContainer, FlexRow, FlexColumn, TitleSection, Title, TitleRow, SubtitleSection, SubtitleRow, Subtitle, LocalizationProvider, 
	Search, PopoverBody, PopoverItem, Popover, PopoverTitle, CircularProgress, NotFound, Button, FilterButton, ButtonGroup, 
	DateRangePicker, TableContainer, Table, TableHeader, TableBody, TableRow, TableColumn, TableHeaderRow, 
	TableHeaderColumn, OrderStatus, TablePagination, SquareButton } from './Styles/Orders';

import { changeTile } from '../../actions/changeTile';
import { getOrderItems, setUploadOrdersFileAction, setUploadOrdersFileBatch, fetchUploadOrdersFileBatch } from '../../actions/ordersActions';
import { getDSOList } from '../../actions/statsActions';
import { formatCurrency, SPJ_ACTIONS } from '../../commons/utils';
import { getOrderStatusLabel, getOrderStatusColor, UploadOrdersFileActions } from './Utils';

const PopoverIds = {
	FILTER_BY_DSO: 'filter-by-dso',
	FILTER_BY_STATUS: 'filter-by-status',
	BULK_ACTIONS: 'bulk-actions-popover',
};
const DialogIds = {
	DOWNLOAD_ORDER_ITEMS: 'download-order-items',
};
const FilterByStatusOptions = [
	{ name: 'All', value: 'ALL', disabled: false },
	{ name: 'Pending', value: 'PENDING', disabled: false },
	{ name: 'Partially Fulfilled', value: 'PARTIALLY_FULFILLED', disabled: false },
	{ name: 'Fulfilled', value: 'FULFILLED', disabled: false },
	{ name: 'Cancelled', value: 'CANCELLED', disabled: false },
	{ name: 'Returned', value: 'RETURNED', disabled: false },
];

class Orders extends Component {
	constructor(props) {
		super(props);
		this.state = {
			dsoOptions: [],
			fetchingDSOOptions: false,
			dateRange: [null, null],
			dateRangePickerOpen: false,
			downloadOrderItemsDialogOpen: false,
			updateTrackingInfoOpen: false,
			bulkActionsPopoverAnchorElement: null,
			pageNumber: 0,
			pageSize: 10,
			selectedOrder: null,
			searchText: '',
			filterStatusPopoverAnchorElement: null,
			filterStatusPopoverItem: '',
			filterDSOPopoverAnchorElement: null,
			filterDSOSearchText: '',
			filterDSOPopoverItem: '',
			custId: null,
		};
	}

	setDefaultState = (next = null) => {
		let urlParams = new URLSearchParams(this.props.location.search);

		let pageSize = parseInt(urlParams.get('page_size'), 10) || 10;
		let pageNumber = parseInt(urlParams.get('page_number'), 10) || 0;
		if (pageNumber) pageNumber = pageNumber - 1;

		let searchText = urlParams.get('search_text') || '';
		let filterDSOPopoverItem = urlParams.get('group_id') || '';
		let filterStatusPopoverItem = urlParams.get('status') || 'ALL';

		let startDate = parseInt(urlParams.get('start_date'), 10) || 0;
		if (startDate) {
			startDate = new Date(startDate);
		} else {
			startDate = new Date();
			startDate.setMonth(startDate.getMonth() - 1);
		}

		let endDate = parseInt(urlParams.get('end_date'), 10) || 0;
		if (endDate) {
			endDate = new Date(endDate);
		} else {
			endDate = new Date();
		}

		this.setState(
			{
				selectedOrder: null,
				pageNumber: pageNumber,
				pageSize: pageSize,
				searchText: searchText,
				filterStatusPopoverAnchorElement: null,
				filterStatusPopoverItem: filterStatusPopoverItem,
				filterDSOPopoverAnchorElement: null,
				filterDSOSearchText: '',
				filterDSOPopoverItem: filterDSOPopoverItem,
				bulkActionsPopoverAnchorElement: null,
				custId: null,
				dateRange: [startDate, endDate],
				dateRangePickerOpen: false,
				downloadOrderItemsDialogOpen: false,
				fetchingUploadOrdersFileBatch: false,
			},
			next
		);
	};

	async componentDidMount() {
		if (this.props.tile !== 'ORDERS') {
			this.props.changeTile('ORDERS');
		}
		if (this.props.history.action === 'POP') {
			this.props.changeTile('ORDERS');
		}

		this.setDefaultState(async () => {});

		await this.getDSOOptions();
		await this.fetchUploadOrdersFileBatch();
	}

	async componentDidUpdate(prevProps, prevState) {
		if (prevProps.supplier && prevProps.supplier !== this.props.supplier) {
			this.setDefaultState(this.refreshOrdersData);
		}

		if (
			prevState.pageNumber !== this.state.pageNumber ||
			prevState.pageSize !== this.state.pageSize ||
			prevState.filterDSOPopoverItem !== this.state.filterDSOPopoverItem ||
			prevState.filterStatusPopoverItem !== this.state.filterStatusPopoverItem ||
			prevState.searchText !== this.state.searchText
		) {
			this.updateURL();
			this.getOrderItems();
		}
	}

	refreshOrdersData = async () => {
		await this.getDSOOptions();
		await this.getOrderItems();
		await this.fetchUploadOrdersFileBatch();
		this.updateURL();
	};

	fetchUploadOrdersFileBatch = async () => {
		this.props.setUploadOrdersFileAction(false);
		this.props.setUploadOrdersFileBatch('');

		this.setState({ fetchingUploadOrdersFileBatch: true });
		await this.props.fetchUploadOrdersFileBatch();
		this.setState({ fetchingUploadOrdersFileBatch: false });
	};

	updateURL = () => {
		let { pageNumber, pageSize, searchText, filterDSOPopoverItem, filterStatusPopoverItem, dateRange } = this.state;

		let newURLParams = {};
		if (pageNumber) newURLParams['page_number'] = pageNumber + 1;
		if (pageSize) newURLParams['page_size'] = pageSize;
		if (searchText) newURLParams['search_text'] = searchText;
		if (filterDSOPopoverItem) newURLParams['group_id'] = filterDSOPopoverItem;
		if (filterStatusPopoverItem) newURLParams['status'] = filterStatusPopoverItem;
		if (dateRange && dateRange[0]) newURLParams['start_date'] = dateRange[0].getTime();
		if (dateRange && dateRange[1]) newURLParams['end_date'] = dateRange[1].getTime();

		this.props.history.push({
			pathname: window.location.pathname,
			search: '?' + new URLSearchParams(newURLParams),
		});
	};

	handlePopoverClick = async (popoverId, event) => {
		switch (popoverId) {
			case PopoverIds.BULK_ACTIONS:
				this.setState({ bulkActionsPopoverAnchorElement: event.currentTarget });
				break;

			case PopoverIds.FILTER_BY_DSO:
				this.setState({ filterDSOPopoverAnchorElement: event.currentTarget });
				break;

			case PopoverIds.FILTER_BY_STATUS:
				this.setState({ filterStatusPopoverAnchorElement: event.currentTarget });
				break;

			default:
				break;
		}
	};

	handlePopoverSelect = async (popoverId, choice) => {
		switch (popoverId) {
			case PopoverIds.BULK_ACTIONS:
				if (choice === 'ADD_BULK_TRACKING_INFO') {
					this.props.setUploadOrdersFileAction(true, SPJ_ACTIONS.SALES_ORDER_UPDATE, UploadOrdersFileActions.UPLOAD);
					this.toggleUpdateTrackingInfoOpen();
				}
				break;

			case PopoverIds.FILTER_BY_DSO:
				this.setState({ filterDSOPopoverItem: choice.value, pageNumber: 0 });
				break;

			case PopoverIds.FILTER_BY_STATUS:
				this.setState({ filterStatusPopoverItem: choice.value, pageNumber: 0 });
				break;

			default:
				break;
		}
		this.handlePopoverClose(popoverId);
	};

	handlePopoverClose = async (popoverId) => {
		switch (popoverId) {
			case PopoverIds.BULK_ACTIONS:
				this.setState({ bulkActionsPopoverAnchorElement: null });
				break;

			case PopoverIds.FILTER_BY_DSO:
				this.setState({ filterDSOPopoverAnchorElement: null, filterDSOSearchText: '' });
				break;

			case PopoverIds.FILTER_BY_STATUS:
				this.setState({ filterStatusPopoverAnchorElement: null });
				break;

			default:
				break;
		}
	};

	handleDialogOnTrue = async (dialogId, value) => {
		switch (dialogId) {
			case DialogIds.DOWNLOAD_ORDER_ITEMS:
				this.setState({ downloadOrderItemsDialogOpen: false });
				break;

			default:
				break;
		}
	};

	handleSearchText = (text = '') => {
		this.setState({ searchText: text, pageNumber: 0 });
	};

	handleDialogOnFalse = (dialogId) => {
		switch (dialogId) {
			case DialogIds.DOWNLOAD_ORDER_ITEMS:
				this.setState({ downloadOrderItemsDialogOpen: false });
				break;

			default:
				break;
		}
	};

	getDSOOptions = async () => {
		this.setState({ fetchingDSOOptions: true });
		let list = await this.props.getDSOList();
		if (list && list.length) {
			let options = [{ name: 'All DSOs', value: '', disabled: false }, ...list.map((dso) => ({ name: dso.group_name, value: dso._id, disabled: false }))];
			this.setState({ fetchingDSOOptions: false, dsoOptions: options || [] });
		} else {
			this.setState({ fetchingDSOOptions: false, dsoOptions: [{ name: 'All DSOs', value: '', disabled: false }] });
		}
	};

	getOrderItems = async () => {
		let { pageNumber, pageSize, searchText, filterStatusPopoverItem, dateRange, filterDSOPopoverItem, custId } = this.state;
		await this.props.getOrderItems(
			pageNumber + 1,
			pageSize,
			filterStatusPopoverItem,
			dateRange[0] ? moment(dateRange[0]).format('YYYY-MM-DD') : null,
			dateRange[1] ? moment(dateRange[1]).format('YYYY-MM-DD') : null,
			custId,
			filterDSOPopoverItem,
			searchText
		);
	};

	onOrderDetailsClose = () => {
		this.setState({ selectedOrder: null }, () => {
			this.getOrderItems();
		});
	};

	handleDateChange = (dateRange) => {
		this.setState({ dateRange: dateRange });
	};

	handleDateSelectOpen = () => {
		this.setState({ dateRangePickerOpen: true });
	};

	handleDateSelectClose = () => {
		this.setState({ dateRangePickerOpen: false });
		this.updateURL();
		setTimeout(() => {
			this.getOrderItems();
		}, 300);
	};

	toggleUpdateTrackingInfoOpen = () => {
		this.setState({ updateTrackingInfoOpen: !this.state.updateTrackingInfoOpen });
	};

	showUploadOrdersFileDialog = () => {
		let { uploadOrdersFileType } = this.props;
		if (uploadOrdersFileType === SPJ_ACTIONS.SALES_ORDER_UPDATE) {
			this.toggleUpdateTrackingInfoOpen();
		}
	};

	render() {
		let { pageNumber, pageSize, dateRange, dateRangePickerOpen, selectedOrder, searchText, filterStatusPopoverAnchorElement, filterStatusPopoverItem, 
			filterDSOPopoverAnchorElement, filterDSOSearchText, fetchingDSOOptions, dsoOptions, filterDSOPopoverItem, downloadOrderItemsDialogOpen, custId, 
			updateTrackingInfoOpen, bulkActionsPopoverAnchorElement } = this.state;
		let { orders, totalResultsCount, totalOrdersCount, pendingOrdersCount, fulfilledOrdersCount, showUploadOrdersFile, disableBulkUploadOrderTracking,
			disableBulkDownloadOrders } = this.props;

		let filterByStatusLabel = (FilterByStatusOptions.find((option) => option.value === filterStatusPopoverItem) || {}).name || '';
		let filterByDSOLabel = (dsoOptions.find((option) => option.value === filterDSOPopoverItem) || {}).name || '';
		let downloadOrdersFilters = {
			searchText: searchText,
			dateRange: dateRange,
			status: { label: filterByStatusLabel, value: filterStatusPopoverItem },
			dso: { label: filterByDSOLabel, value: filterDSOPopoverItem },
			custId: custId,
		};

		return (
			<>
				{selectedOrder ? (
					<OrderDetails
						selectedOrder={selectedOrder}
						onClose={this.onOrderDetailsClose}
					/>
				) : null}

				<RootContainer style={{ display: !selectedOrder ? 'block' : 'none' }}>
					<Helmet>
						<title>Seller Portal | Orders</title>
					</Helmet>

					<DownloadOrderItems
						key={DialogIds.DOWNLOAD_ORDER_ITEMS}
						open={downloadOrderItemsDialogOpen}
						filters={downloadOrdersFilters}
						onTrue={() => {
							this.handleDialogOnTrue(DialogIds.DOWNLOAD_ORDER_ITEMS);
						}}
						onFalse={() => {
							this.handleDialogOnFalse(DialogIds.DOWNLOAD_ORDER_ITEMS);
						}}
					/>

					<Popover
						id={PopoverIds.FILTER_BY_DSO}
						open={Boolean(filterDSOPopoverAnchorElement)}
						anchorEl={filterDSOPopoverAnchorElement}
						onClose={() => this.handlePopoverClose(PopoverIds.FILTER_BY_DSO)}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'center',
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'center',
						}}
					>
						<PopoverTitle>Select DSO</PopoverTitle>

						<PopoverItem>
							<Search
								searchText={filterDSOSearchText}
								placeholder={'Search DSO'}
								searchOnChange={true}
								onSearchHandler={(value) => this.setState({ filterDSOSearchText: value })}
							/>
						</PopoverItem>

						<PopoverBody>
							{fetchingDSOOptions ? (
								<CircularProgress
									showSpinner={fetchingDSOOptions}
									size={40}
								/>
							) : (
								dsoOptions.map((item, index) => {
									return item.name.toUpperCase().includes((filterDSOSearchText || '').toUpperCase()) ? (
										<PopoverItem
											key={index}
											disabled={item.disabled || item.value === filterDSOPopoverItem}
											onClick={(e) => this.handlePopoverSelect(PopoverIds.FILTER_BY_DSO, item)}
										>
											{item.name}
										</PopoverItem>
									) : null;
								})
							)}
						</PopoverBody>
					</Popover>

					<Popover
						id={PopoverIds.FILTER_BY_STATUS}
						open={Boolean(filterStatusPopoverAnchorElement)}
						anchorEl={filterStatusPopoverAnchorElement}
						onClose={() => this.handlePopoverClose(PopoverIds.FILTER_BY_STATUS)}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'center',
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'center',
						}}
					>
						<PopoverTitle>Select Order Status</PopoverTitle>

						<PopoverBody style={{ minWidth: '120px' }}>
							{FilterByStatusOptions.map((item, index) => (
								<PopoverItem
									key={index}
									disabled={item.disabled || item.value === filterStatusPopoverItem}
									onClick={(e) => this.handlePopoverSelect(PopoverIds.FILTER_BY_STATUS, item)}
								>
									{item.name}
								</PopoverItem>
							))}
						</PopoverBody>
					</Popover>

					<Popover
						id={PopoverIds.BULK_ACTIONS}
						open={Boolean(bulkActionsPopoverAnchorElement)}
						anchorEl={bulkActionsPopoverAnchorElement}
						onClose={() => this.handlePopoverClose(PopoverIds.BULK_ACTIONS)}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						transformOrigin={{ vertical: 'top', horizontal: 'right' }}
					>
						<PopoverItem
							disabled={showUploadOrdersFile}
							onClick={(e) => {
								this.handlePopoverSelect(PopoverIds.BULK_ACTIONS, 'ADD_BULK_TRACKING_INFO');
							}}
						>
							Update Order Status And Tracking Info
						</PopoverItem>
					</Popover>

					{updateTrackingInfoOpen ? (
						<UpdateTrackingInfo
							open={updateTrackingInfoOpen}
							dsoOptions={dsoOptions}
							toggle={this.toggleUpdateTrackingInfoOpen}
							onSuccess={this.getOrderItems}
						/>
					) : null}

					<TitleSection>
						<TitleRow responsive='1200px'>
							<FlexColumn style={{ flex: 'auto' }}>
								<FlexRow style={{ gap: '2rem', justifyContent: 'space-between' }}>
									<Title>Orders</Title>

									<Search
										searchText={searchText}
										placeholder={'Search Orders With Order ID, PO ID Or Email'}
										enableDelayOnChange={true}
										searchOnChange={true}
										onSearchHandler={(value) => {
											this.handleSearchText(value);
										}}
										style={{ justifyContent: 'right' }}
									/>
								</FlexRow>
							</FlexColumn>

							<FlexColumn style={{ alignSelf: 'center' }}>
								<ButtonGroup alignRight>
									<FilterButton
										primary
										outline
										onClick={(e) => this.handlePopoverClick(PopoverIds.FILTER_BY_DSO, e)}
									>
										<DSOIcon />
										{filterByDSOLabel}
										<ArrowDownIcon />
									</FilterButton>

									<LocalizationProvider>
										<DateRangePicker
											calendars={2}
											value={dateRange}
											open={dateRangePickerOpen}
											disableCloseOnSelect={false}
											disableFuture
											orientation={'landscape'}
											disableHighlightToday={true}
											onChange={(dateRange) => this.handleDateChange(dateRange)}
											onClose={() => this.handleDateSelectClose()}
											renderInput={(startProps, endProps) => (
												<FilterButton
													primary
													outline
													onClick={(e) => {
														this.handleDateSelectOpen();
													}}
												>
													<CalendarIcon />
													{dateRange && dateRange[0] && dateRange[1]
														? `${dateRange[0].toLocaleString('default', { month: 'short' })} ${dateRange[0].getDate()} - ${dateRange[1].toLocaleString('default', {
																month: 'short',
														  })} ${dateRange[1].getDate()}`
														: 'Select Date'}
													<ArrowDownIcon />
												</FilterButton>
											)}
										/>
									</LocalizationProvider>

									<FilterButton
										primary
										outline
										onClick={(e) => this.handlePopoverClick(PopoverIds.FILTER_BY_STATUS, e)}
									>
										<StatusIcon />
										{filterByStatusLabel}
										<ArrowDownIcon />
									</FilterButton>

									{!disableBulkUploadOrderTracking ? (
										showUploadOrdersFile ? (
											<Button
												secondary
												onClick={(e) => this.showUploadOrdersFileDialog()}
											>
												<ProcessingIcon />
												Show Upload Progress
											</Button>
										) : (
											<Button
												primary
												onClick={(e) => this.handlePopoverClick(PopoverIds.BULK_ACTIONS, e)}
											>
												<AddIcon />
												Bulk Actions
											</Button>
										)
									) : null}

									{!disableBulkDownloadOrders ? (
										<Button
											secondary
											disabled={false}
											onClick={(e) => this.setState({ downloadOrderItemsDialogOpen: true })}
										>
											<DownloadIcon />
											Download Orders
										</Button>
									) : null}

									<SquareButton
										secondary
										onClick={(e) => {
											this.refreshInventoryData();
										}}
									>
										<RefreshInventoryIcon />
									</SquareButton>
								</ButtonGroup>
							</FlexColumn>
						</TitleRow>
					</TitleSection>

					<SubtitleSection>
						<SubtitleRow>
							<FlexColumn style={{ flex: 1 }}>
								<Subtitle>
									{`${totalOrdersCount || 0} ${(totalOrdersCount || 0) === 1 ? 'Order' : 'Orders'} • `}
									{`${fulfilledOrdersCount || 0} Fulfilled • `}
									{`${pendingOrdersCount || 0} Unfulfilled`}
								</Subtitle>
							</FlexColumn>
						</SubtitleRow>
					</SubtitleSection>

					<TableContainer>
						<Table>
							<TableHeader>
								<TableHeaderRow>
									<TableHeaderColumn style={{ width: '15%' }}>ORDER ID</TableHeaderColumn>

									<TableHeaderColumn style={{ width: '15%' }}>PO ID</TableHeaderColumn>

									<TableHeaderColumn style={{ width: '15%' }}>CREATED</TableHeaderColumn>

									<TableHeaderColumn style={{ width: '20%' }}>CUSTOMER</TableHeaderColumn>

									<TableHeaderColumn
										alignCenter
										style={{ width: '10%' }}
									>
										# OF ITEMS
									</TableHeaderColumn>

									<TableHeaderColumn
										alignRight
										style={{ width: '10%' }}
									>
										ORDER TOTAL
									</TableHeaderColumn>

									<TableHeaderColumn
										alignCenter
										style={{ width: '15%' }}
									>
										STATUS
									</TableHeaderColumn>
								</TableHeaderRow>
							</TableHeader>

							<TableBody>
								{orders && orders.length ? (
									orders.map((order, index) => (
										<TableRow
											key={index}
											onClick={() => this.setState({ selectedOrder: order })}
										>
											<TableColumn>{order.order_id}</TableColumn>

											<TableColumn>{order.po_id || "-"}</TableColumn>

											<TableColumn>{moment(order.order_date).fromNow()}</TableColumn>

											<TableColumn>{order.userFullName ? order.userFullName : 'Not Specified'}</TableColumn>

											<TableColumn alignCenter>{order.no_of_products}</TableColumn>

											<TableColumn
												alignRight
												style={{ color: Theme.orange }}
											>
												{formatCurrency(order.total)}
											</TableColumn>

											<TableColumn alignCenter>
												<OrderStatus style={{ backgroundColor: getOrderStatusColor(order.status) }}>{getOrderStatusLabel(order.status)}</OrderStatus>
											</TableColumn>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableColumn colSpan={'100%'}>
											<NotFound lines={["Couldn't find any orders"]} />
										</TableColumn>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<TablePagination
						component='div'
						count={totalResultsCount ? totalResultsCount : 0}
						page={pageNumber}
						onPageChange={(e, page) => {
							this.setState({ pageNumber: parseInt(page, 10) });
						}}
						rowsPerPage={`${pageSize}`}
						onRowsPerPageChange={(e) => {
							this.setState({ pageSize: parseInt(e.target.value, 10) });
						}}
					/>
				</RootContainer>
			</>
		);
	}
}

const mapStateToProps = (state) => {
	let supplierSellerPortalConfig = state.supplier.sellerPortalConfig || {};
	let userSellerPortalConfig = state.session.sellerPortalConfig || {};

	let disableBulkUploadOrderTracking = supplierSellerPortalConfig.disable_bulk_upload_order_tracking || userSellerPortalConfig.disable_bulk_upload_order_tracking || false;
	let disableBulkDownloadOrders = supplierSellerPortalConfig.disable_bulk_download_orders || userSellerPortalConfig.disable_bulk_download_orders || false;

	return {
		disableBulkUploadOrderTracking,
		disableBulkDownloadOrders,
		totalResultsCount: state.orders.order_items ? state.orders.order_items.totalResultCount : 0,
		totalOrdersCount: state.orders.order_items ? state.orders.order_items.totalOrdersCount : 0,
		pendingOrdersCount: state.orders.order_items ? state.orders.order_items.pendingOrdersCount : 0,
		fulfilledOrdersCount: state.orders.order_items ? state.orders.order_items.fulfilledOrdersCount : 0,
		orders: state.orders.order_items ? state.orders.order_items.orders : [],
		tile: state.tiles.tile,
		supplier: state.supplier.supplier,
		showUploadOrdersFile: (state.orders.showUploadOrdersFile && state.orders.uploadOrdersFileAction !== UploadOrdersFileActions.UPLOAD) || false,
		uploadOrdersFileAction: state.orders.uploadOrdersFileAction || '',
		uploadOrdersFileBatch: state.orders.uploadOrdersFileBatch || '',
		uploadOrdersFileType: state.orders.uploadOrdersFileType || '',
	};
};

const mapDispatchToProps = () => {
	return {
		changeTile,
		getOrderItems,
		getDSOList,
		setUploadOrdersFileAction,
		setUploadOrdersFileBatch,
		fetchUploadOrdersFileBatch,
	};
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps())(Orders));
